import { useState } from "react";
import { makeStyles } from "@material-ui/core";
import moment from "moment";
import voca from "voca";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import InsertInvitationIcon from "@material-ui/icons/InsertInvitation";
import Modal from "@material-ui/core/Modal";

import { InputButton } from "../input-button/InputButton";
import { InputField } from "../input-field/InputField";
import { InputSelect } from "../input-select/InputSelect";
import { ViewDocumentInput } from "../view_document_input/ViewDocumentInput";
import { DDDD_MMMM_YYYY_FORMAT } from "../../settings/constants";
import { IdentificationTypeIdEnum } from "../../attributes/user-attributes";

interface MyAccountProps {
  firstName: string;
  lastName: string;
  email: string;
  primaryPhone: string;
  secondaryPhone: string;
  identificationType: string;
  identificationNumber: string;
  identificationIssueDate: string;
  identificationExpirationDate: string;
  paths: any[];
}

const useStyles = makeStyles((theme) => ({
  main: {
    width: "100%",
    height: "100%",
    padding: 20,
    flexGrow: 1,
  },
  formSectionTitle: {
    fontWeight: 400,
    fontSize: 24,
  },
  spacer: {
    width: "100%",
    height: 40,
  },
  deleteNotice: {
    fontSize: 17,
    color: theme.palette.grey[400],
  },
  deleteButton: {
    fontSize: 17,
    backgroundColor: "#FF2929",
    color: "#FFFFFF",
    textTransform: "none",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  zoomed: {
    width: "60vw",
    height: "70vh",
  },
}));

export const MyAccount = (props: MyAccountProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const {
    firstName,
    lastName,
    email,
    primaryPhone,
    secondaryPhone,
    identificationType,
    identificationNumber,
    identificationIssueDate,
    identificationExpirationDate,
    paths,
  } = props;
  const [editing] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  const viewDocument = (image: string) => {
    setZoomImage(image);
    setZoomed(true);
  };

  return (
    <>
      <Modal
        className={classes.modal}
        open={zoomed}
        onClose={() => setZoomed(false)}
      >
        <img className={classes.zoomed} alt="document" src={zoomImage} />
      </Modal>
      <Paper className={classes.main}>
        <Grid container spacing={2}>
          <Grid
            item
            container
            xs={12}
            sm={12}
            lg={12}
            justify="space-between"
            direction="row"
          >
            <Typography
              className={classes.formSectionTitle}
              variant="h4"
              component="h4"
            >
              <FormattedMessage id="sharedScreen.myAccount" />
            </Typography>
            <InputButton
              color="secondary"
              variant={editing ? "contained" : "outlined"}
            >
              <FormattedMessage id="sharedScreen.edit" />
            </InputButton>
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={firstName}
              label={formatMessage({ id: "sharedScreen.firstName" })}
              fullWidth
              readOnly={!editing}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={lastName}
              label={formatMessage({ id: "sharedScreen.lastName" })}
              fullWidth
              readOnly={!editing}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={email}
              label={formatMessage({ id: "sharedScreen.email" })}
              fullWidth
              readOnly={!editing}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={primaryPhone}
              label={formatMessage({ id: "sharedScreen.primaryPhoneNumber" })}
              fullWidth
              readOnly={!editing}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={secondaryPhone}
              label={formatMessage({ id: "sharedScreen.secondaryPhoneNumber" })}
              fullWidth
              readOnly={!editing}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <InputSelect
              value={identificationType}
              label={formatMessage({ id: "sharedScreen.idType" })}
              fullWidth
              readOnly={!editing}
            >
              <MenuItem value={IdentificationTypeIdEnum.cni}>
                <FormattedMessage id="sharedScreen.nationalIdentityCard" />
              </MenuItem>
              <MenuItem value={IdentificationTypeIdEnum.passport}>
                <FormattedMessage id="sharedScreen.passport" />
              </MenuItem>
            </InputSelect>
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={identificationNumber}
              label={formatMessage({ id: "sharedScreen.idNumber" })}
              fullWidth
              readOnly={!editing}
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={voca.titleCase(
                moment(identificationIssueDate).format(DDDD_MMMM_YYYY_FORMAT)
              )}
              label={formatMessage({ id: "sharedScreen.idCreationDate" })}
              fullWidth
              readOnly={!editing}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <InsertInvitationIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <InputField
              value={voca.titleCase(
                moment(identificationExpirationDate).format(
                  DDDD_MMMM_YYYY_FORMAT
                )
              )}
              label={formatMessage({ id: "sharedScreen.idExpiryDate" })}
              fullWidth
              readOnly={!editing}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton edge="end">
                    <InsertInvitationIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} lg={12} />
          <Grid item xs={12} sm={12} lg={12}>
            <Typography
              className={classes.formSectionTitle}
              variant="h4"
              component="h4"
            >
              <FormattedMessage id="sharedScreen.attachedDocuments" />
            </Typography>
          </Grid>
          {paths.length && (
            <ViewDocumentInput
              label={formatMessage({ id: "sharedScreen.frontPictureOfId" })}
              viewCallback={() => viewDocument(paths[0])}
              selectCallback={null}
            />
          )}
          {paths.length > 1 && (
            <ViewDocumentInput
              label={formatMessage({ id: "sharedScreen.backPictureOfId" })}
              viewCallback={() => viewDocument(paths[1])}
              selectCallback={null}
            />
          )}
          <Grid item xs={12} sm={12} lg={12}>
            <div className={classes.spacer} />
          </Grid>
          <Grid item container xs={12} sm={12} lg={12} justify="center">
            <Typography
              className={classes.deleteNotice}
              variant="h4"
              component="h4"
            >
              <FormattedMessage id="sharedScreen.deleteAccountNotice" />
            </Typography>
          </Grid>
          <Grid item container xs={12} sm={12} lg={12} justify="center">
            <InputButton className={classes.deleteButton} disabled>
              <FormattedMessage id="sharedScreen.deleteAccount" />
            </InputButton>
          </Grid>
          <Grid item xs={12} sm={12} lg={12}>
            <div className={classes.spacer} />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};
